import React, { useState, useEffect } from "react";
import { C, fmt, statusLabel, text } from "../theme";
import { usePermissions } from "../PermissionContext";
import { api } from "../api";
import { Card, Btn, Notice, ConfirmDialog, DocLink, StatusPill as Badge, ApprovalTrail, rowAction } from "../components/ui";
import DateField from "../components/DateField";

// Banding runs on the invoice's GST-inclusive gross (UX-57) — say so.
const TIERS = [
  { tier: 1, label: "Tier 1 — gross under $10,000" },
  { tier: 2, label: "Tier 2 — gross $10,000 to $80,000" },
  { tier: 3, label: "Tier 3 — gross $80,000 to $200,000" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function PaymentsPage({ initialId, onOpenDoc, onNavigate }) {
  const { me, can } = usePermissions();
  const [view, setView] = useState("list"); // list | new | detail
  const [batches, setBatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const loadList = () => {
    setLoading(true);
    api.getPaymentBatches(statusFilter).then(setBatches).catch(e => setNotice({ kind: "error", text: e.message })).finally(() => setLoading(false));
  };
  useEffect(loadList, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = (id) => {
    api.getPaymentBatch(id).then(b => { setDetail(b); setView("detail"); }).catch(e => setNotice({ kind: "error", text: e.message }));
  };
  // Deep link from the Approvals card or dashboard: open that batch on mount.
  useEffect(() => { if (initialId) openDetail(initialId); }, [initialId]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshDetail = () => detail && api.getPaymentBatch(detail.id).then(setDetail).catch(() => {});

  if (loading && view === "list") return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;
  if (view === "new") return <NewRun onCancel={() => setView("list")} onCreated={(b) => { loadList(); openDetail(b.id); }} onNavigate={onNavigate} onOpenDoc={onOpenDoc} />;
  if (view === "detail" && detail) return <Detail batch={detail} me={me} can={can} onBack={() => { setView("list"); setDetail(null); loadList(); }} reload={refreshDetail} onOpenDoc={onOpenDoc} />;

  // ─── List ───
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Payment Runs</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>{batches.length} batches</p>
        </div>
        {can("payment", "prepare") && <Btn onClick={() => { setNotice(null); setView("new"); }}>+ New Payment Run</Btn>}
      </div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div style={{ marginBottom: 12 }}>
        <select aria-label="Filter by status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md }}>
          <option value="">All statuses</option>
          {["DRAFT", "PENDING_APPROVAL", "APPROVED", "PROCESSING", "PAID", "FAILED", "CANCELLED"].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
      </div>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Batch No.", "Tier", "Payment Date", "Items", "Total", "Status"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {batches.map(b => (
              <tr key={b.id} {...rowAction(() => openDetail(b.id))} style={{ cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.gray50}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>
                  {b.batchNumber}
                  {b.isCherryPicked && <span title="Cherry-picked / exception" style={{ marginLeft: 6 }}>⚑</span>}
                  {b.overrideRequestReason && !b.isCherryPicked && b.status === "DRAFT" && <span title="Override requested — awaiting approver grant" style={{ marginLeft: 6 }}>⏳⚑</span>}
                </td>
                <td style={{ padding: "10px 12px" }}>{b.tier}</td>
                <td style={{ padding: "10px 12px" }}>{new Date(b.paymentDate).toLocaleDateString()}</td>
                <td style={{ padding: "10px 12px" }}>{b.itemCount}</td>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{fmt(b.totalAmount)}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={b.status} /></td>
              </tr>
            ))}
            {batches.length === 0 && <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: C.gray500 }}>No payment batches yet — prepare one from approved, Xero-pushed invoices with + New Payment Run.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ─── New payment run wizard ───
function NewRun({ onCancel, onCreated, onNavigate, onOpenDoc }) {
  const [tier, setTier] = useState(1);
  const [paymentDate, setPaymentDate] = useState(today());
  const [banks, setBanks] = useState([]);
  const [bankId, setBankId] = useState("");
  const [eligible, setEligible] = useState([]);
  // A failed sieve is an ERROR, not an empty tier (UX-13): keep the real
  // message (e.g. the 409 "Xero is not connected") and render it as such.
  const [eligibleErr, setEligibleErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  useEffect(() => { api.xeroBankAccounts().then(setBanks).catch(() => setBanks([])); }, []);
  useEffect(() => {
    setEligibleErr("");
    api.getEligibleInvoices(tier).then(setEligible).catch((e) => { setEligible([]); setEligibleErr(e.message); });
  }, [tier]);

  const total = eligible.reduce((s, i) => s + Number(i.amountDue != null ? i.amountDue : i.grossTotal), 0);

  const create = async () => {
    if (!bankId) { setNotice({ kind: "error", text: "Select a bank account." }); return; }
    setBusy(true);
    try {
      const b = await api.createPaymentBatch({ tier, paymentDate, xeroBankAccountId: bankId });
      onCreated(b);
    } catch (e) { setNotice({ kind: "error", text: e.message }); } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <Btn variant="outline" onClick={onCancel}>← Cancel</Btn>
        <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>New Payment Run</h1>
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <div>
            <label style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }} htmlFor="pay-tier">Tier</label>
            <select id="pay-tier" value={tier} onChange={e => setTier(Number(e.target.value))} style={{ width: "100%", padding: "10px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md }}>
              {TIERS.map(t => <option key={t.tier} value={t.tier}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }} htmlFor="pay-date">Payment Date</label>
            <DateField id="pay-date" value={paymentDate} onChange={setPaymentDate} />
          </div>
          <div>
            <label style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }} htmlFor="pay-bank">Bank Account</label>
            <select id="pay-bank" value={bankId} onChange={e => setBankId(e.target.value)} style={{ width: "100%", padding: "10px", border: `1px solid ${bankId ? C.gray300 : C.danger}`, borderRadius: 6, fontSize: text.md }}>
              <option value="">— select —</option>
              {banks.map(b => <option key={b.xeroAccountId} value={b.xeroAccountId}>{b.code} · {b.name}{b.currencyCode ? ` (${b.currencyCode})` : ""}</option>)}
            </select>
            {banks.length === 0 && (
              <div style={{ fontSize: text.xs, color: C.danger, marginTop: 4 }}>
                No bank accounts synced from Xero — connect or re-sync Xero
                {onNavigate ? <> in <DocLink onClick={() => onNavigate("settings")} style={{ fontSize: "inherit" }}>Settings</DocLink></> : " in Settings"} first.
              </div>
            )}
          </div>
        </div>
      </Card>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700 }}>Eligible invoices (auto-sieved)</div>
          <div style={{ fontSize: text.md, color: C.gray600 }}>{eligible.length} invoices · <strong>{fmt(total)}</strong></div>
        </div>
        {/* What the sieve actually does (UX-13). */}
        <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 10 }}>
          The sieve picks every approved invoice that has been pushed to Xero, is unpaid (or partially paid),
          falls in this tier's gross band, and isn't already in another active batch. The whole set is paid together — cherry-picking needs an approver override.
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Invoice No.", "Vendor", "Gross", "Remaining"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {eligible.map(i => (
              <tr key={i.id} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "8px 10px", fontWeight: 600 }}>
                  {onOpenDoc ? <DocLink onClick={() => onOpenDoc("invoice", i.id)}>{i.invoiceNumber}</DocLink> : <span style={{ color: C.primary }}>{i.invoiceNumber}</span>}
                </td>
                <td style={{ padding: "8px 10px" }}>{i.vendor}</td>
                <td style={{ padding: "8px 10px" }}>{fmt(i.grossTotal)}</td>
                <td style={{ padding: "8px 10px" }}>{fmt(i.amountDue != null ? i.amountDue : i.grossTotal)}</td>
              </tr>
            ))}
            {eligible.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 24, textAlign: "center", color: eligibleErr ? C.danger : C.gray500 }}>
                  {eligibleErr
                    ? <span role="alert" style={{ fontWeight: 600 }}>⚠ Couldn't load eligible invoices: {eligibleErr}{/^xero/i.test(eligibleErr) && onNavigate ? <> — <DocLink onClick={() => onNavigate("settings")} style={{ fontSize: "inherit" }}>open Settings</DocLink></> : null}</span>
                    : "No eligible invoices in this tier — an invoice becomes eligible once it is approved and pushed to Xero."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      {/* Anchored to the action row so errors appear where the user acted. */}
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Btn onClick={create} disabled={busy || !bankId || eligible.length === 0}>{busy ? "Creating..." : `Create Draft — ${eligible.length} invoices, ${fmt(total)}`}</Btn>
    </div>
  );
}

// ─── Batch detail ───
function Detail({ batch, me, can, onBack, reload, onOpenDoc }) {
  const b = batch;
  const isDraft = b.status === "DRAFT";
  const myOverride = b.override && !b.override.consumed && me && b.override.grantedToId === me.id ? b.override : null;
  // A persisted, un-granted request shows as a pending state (UX-41).
  const pendingRequest = b.overrideRequestReason && (!b.override || b.override.consumed) ? {
    reason: b.overrideRequestReason,
    by: b.overrideRequestedBy?.displayName,
    at: b.overrideRequestedAt,
  } : null;
  const [removeIds, setRemoveIds] = useState([]);
  const [partials, setPartials] = useState({}); // itemId -> amount
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }
  const [confirming, setConfirming] = useState(null); // "request" | "grant" | "cancel" | "execute" | "submit" | null

  const act = async (fn) => {
    setConfirming(null);
    try { const r = await fn(); if (r && r.message) setNotice({ kind: "success", text: r.message }); reload(); }
    catch (e) {
      if (e.message) setNotice({ kind: "error", text: e.message });
      reload();
    }
  };

  const requestOverride = (reason) => act(async () => {
    const r = await api.requestPaymentOverride(b.id, reason);
    return { ...r, message: "Override requested — it now shows on this batch until a Payment Approver grants (or the batch is submitted/cancelled)." };
  });
  const grantOverride = (reason) => act(async () => {
    await api.grantPaymentOverride(b.id, { reason });
    return { message: "Override granted — the requester can now remove invoices or set partial amounts on this draft (valid 24h, one submission)." };
  });
  const applyEdits = async () => {
    const partialList = Object.entries(partials)
      .filter(([, v]) => v !== "" && v != null)
      .map(([itemId, payAmount]) => ({ itemId, payAmount: Number(payAmount) }));
    await act(() => api.editPaymentBatchItems(b.id, { remove: removeIds, partials: partialList }));
    setRemoveIds([]); setPartials({});
  };
  const submit = () => act(async () => {
    const r = await api.submitPaymentBatch(b.id);
    window.dispatchEvent(new Event("workqueue-changed"));
    return { ...r, message: "Batch submitted for approval." };
  });
  const cancel = () => act(async () => {
    const r = await api.cancelPaymentBatch(b.id);
    window.dispatchEvent(new Event("workqueue-changed"));
    return { ...r, message: "Batch cancelled — its invoices are released back to the sieve." };
  });
  const execute = async () => {
    setConfirming(null);
    try { await api.executePaymentBatch(b.id); setNotice({ kind: "success", text: "Payment executed." }); reload(); }
    catch (e) {
      try {
        const r = await fetch(`/api/payments/batches/${b.id}/execute`, { method: "POST" });
        const data = await r.json();
        if (data.problems) setNotice({ kind: "error", text: "Cannot execute — these bills changed in Xero:\n" + data.problems.map(p => `${p.xeroBillId}: ${p.reason}`).join("\n") });
        else setNotice({ kind: "error", text: data.error || e.message });
      } catch { setNotice({ kind: "error", text: e.message }); }
      reload();
    }
  };
  const downloadFile = async () => {
    try { const { url } = await api.getPaymentBankFile(b.id); window.location = url; }
    catch (e) { setNotice({ kind: "error", text: e.message }); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <Btn variant="outline" onClick={onBack} style={{ padding: "4px 10px", fontSize: text.xs }}>← Back</Btn>
        <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{b.batchNumber}</h1>
        <Badge status={b.status} />
        {b.isCherryPicked && <span style={{ fontSize: text.xs, fontWeight: 700, color: C.purple }}>⚑ Exception</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {isDraft && can("payment", "prepare") && <Btn variant="success" onClick={() => setConfirming("submit")}>Submit for Approval</Btn>}
          {isDraft && can("payment", "prepare") && !pendingRequest && !myOverride && <Btn variant="outline" onClick={() => setConfirming("request")}>Request Override</Btn>}
          {isDraft && can("payment", "approve") && <Btn variant="purple" onClick={() => setConfirming("grant")}>Grant Override</Btn>}
          {/* The server allows cancelling a submitted batch too (UX-44): recall
              it before an approver acts, releasing its invoices to the sieve. */}
          {["DRAFT", "PENDING_APPROVAL"].includes(b.status) && can("payment", "prepare") && <Btn variant="danger" onClick={() => setConfirming("cancel")}>{isDraft ? "Cancel" : "Recall (cancel)"}</Btn>}
          {b.status === "APPROVED" && can("payment", "execute") && <Btn variant="success" onClick={() => setConfirming("execute")}>Execute Payment →</Btn>}
          {b.bankFilePath && <Btn variant="outline" onClick={downloadFile}>Download Bank File</Btn>}
        </div>
      </div>

      <ConfirmDialog
        open={confirming === "submit"}
        title="Submit this batch for approval?"
        confirmLabel="Submit"
        onConfirm={submit}
        onCancel={() => setConfirming(null)}
      >
        {`${b.itemCount} invoice${b.itemCount === 1 ? "" : "s"} totalling ${fmt(b.totalAmount)} go to the Payment Approver. The batch locks for editing; you can still recall (cancel) it until an approver acts.`}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "request"}
        title="Request a cherry-pick override"
        confirmLabel="Request override"
        requireReason
        reasonLabel="Why must this run deviate from the full sieve? (seen by the approver and kept on the batch)"
        onConfirm={(reason) => requestOverride(reason)}
        onCancel={() => setConfirming(null)}
      >
        {"A payment run normally pays every sieved invoice in full. Removing invoices or paying partial amounts needs a Payment Approver's authorisation — your reason below is stored on the batch and shown to them."}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "grant"}
        title="Grant a cherry-pick override"
        confirmLabel="Grant override"
        variant="purple"
        requireReason
        reasonLabel="Authorisation reason (recorded with the batch)"
        onConfirm={(reason) => grantOverride(reason)}
        onCancel={() => setConfirming(null)}
      >
        {pendingRequest
          ? `${pendingRequest.by || "The preparer"} requested: "${pendingRequest.reason}"\n\nGranting lets them remove invoices or set partial amounts on this draft. Both reasons stay on the record.`
          : "No pending request from the preparer — you can still grant proactively. Granting lets the preparer remove invoices or set partial amounts on this draft."}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "cancel"}
        title={isDraft ? "Cancel this batch?" : "Recall this submitted batch?"}
        confirmLabel={isDraft ? "Cancel batch" : "Recall batch"}
        variant="danger"
        onConfirm={cancel}
        onCancel={() => setConfirming(null)}
      >
        {`${b.batchNumber} is cancelled and its ${b.itemCount} invoice${b.itemCount === 1 ? "" : "s"} return to the eligible sieve for a future run.${b.status === "PENDING_APPROVAL" ? " The pending approval is withdrawn." : ""}`}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "execute"}
        title="Execute this payment?"
        confirmLabel="Execute payment"
        variant="success"
        onConfirm={execute}
        onCancel={() => setConfirming(null)}
      >
        {`${fmt(b.totalAmount)} is paid from ${b.bankAccountCode || "the selected account"}: the payment is recorded in Xero, the invoices are marked paid, and the bank file is generated. This cannot be undone in-system.`}
      </ConfirmDialog>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {/* Pending override request (UX-41): visible to everyone on the batch,
          so the approver has an in-system signal instead of a hallway ask. */}
      {pendingRequest && isDraft && (
        <Card role="status" style={{ marginBottom: 16, background: C.warningBg, border: `1px solid ${C.warningLight}` }}>
          <div style={{ fontSize: text.md, fontWeight: 700, color: C.warning, marginBottom: 4 }}>⏳ Override requested — awaiting approver grant</div>
          <div style={{ fontSize: text.sm, color: C.gray700, lineHeight: 1.5 }}>
            {pendingRequest.by || "The preparer"}{pendingRequest.at ? ` on ${new Date(pendingRequest.at).toLocaleString()}` : ""}: “{pendingRequest.reason}”
            {can("payment", "approve") ? " — use Grant Override above to authorise it." : " A Payment Approver must grant it before the batch can be cherry-picked."}
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, fontSize: text.md }}>
          <div><span style={{ color: C.gray500 }}>Tier:</span> <strong>{b.tier}</strong></div>
          <div><span style={{ color: C.gray500 }}>Payment Date:</span> <strong>{new Date(b.paymentDate).toLocaleDateString()}</strong></div>
          <div><span style={{ color: C.gray500 }}>Bank Account:</span> <strong>{b.bankAccountCode || "—"}</strong></div>
          <div><span style={{ color: C.gray500 }}>Items:</span> <strong>{b.itemCount}</strong></div>
          <div><span style={{ color: C.gray500 }}>Total:</span> <strong style={{ fontSize: text.h3 }}>{fmt(b.totalAmount)}</strong></div>
          {b.xeroBatchPaymentId && <div><span style={{ color: C.gray500 }}>Xero Batch:</span> <strong>{b.xeroBatchPaymentId}</strong></div>}
        </div>
        {b.override && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: C.gray50, borderRadius: 8, fontSize: text.sm, lineHeight: 1.6 }}>
            <div><strong>Override {b.override.consumed ? "(consumed)" : "(active)"}</strong> — granted{b.override.grantedBy ? ` by ${b.override.grantedBy.displayName}` : ""}{b.override.grantedTo ? ` to ${b.override.grantedTo.displayName}` : ""}: {b.override.reason}</div>
            {/* Keep the requester's own words alongside the grant (UX-41). */}
            {b.overrideRequestReason && <div style={{ color: C.gray600 }}>Requested{b.overrideRequestedBy ? ` by ${b.overrideRequestedBy.displayName}` : ""}: {b.overrideRequestReason}</div>}
          </div>
        )}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700 }}>Invoices in this batch</div>
          {myOverride && <div style={{ fontSize: text.sm, color: C.purple }}>Override active — remove or set partial amounts, then Apply</div>}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["", "Invoice No.", "Vendor", "Gross", "Pay Amount", "Status"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {(b.items || []).map(it => (
              <tr key={it.id} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "8px 10px", width: 28 }}>
                  {myOverride && <input type="checkbox" aria-label={`Remove ${it.invoice?.invoiceNumber || "invoice"} from batch`} checked={removeIds.includes(it.id)} onChange={e => setRemoveIds(e.target.checked ? [...removeIds, it.id] : removeIds.filter(x => x !== it.id))} />}
                </td>
                <td style={{ padding: "8px 10px", fontWeight: 600 }}>
                  {/* Real link (UX-43): the Preparer can open what they commit to paying. */}
                  {onOpenDoc && (it.invoice?.id || it.invoiceId)
                    ? <DocLink onClick={() => onOpenDoc("invoice", it.invoice?.id || it.invoiceId)}>{it.invoice?.invoiceNumber}</DocLink>
                    : <span style={{ color: C.primary }}>{it.invoice?.invoiceNumber}</span>}
                </td>
                <td style={{ padding: "8px 10px" }}>{it.invoice?.vendor}</td>
                <td style={{ padding: "8px 10px" }}>{fmt(it.grossAmount)}</td>
                <td style={{ padding: "8px 10px" }}>
                  {myOverride ? (
                    <input type="number" min="0" step="0.01" aria-label={`Pay amount for ${it.invoice?.invoiceNumber || "invoice"}`} placeholder={String(it.payAmount)} value={partials[it.id] ?? ""}
                      onChange={e => setPartials({ ...partials, [it.id]: e.target.value })}
                      style={{ width: 110, padding: "5px 8px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, boxSizing: "border-box" }} />
                  ) : fmt(it.payAmount)}
                </td>
                <td style={{ padding: "8px 10px" }}><Badge status={it.status} /></td>
              </tr>
            ))}
            {(b.items || []).length === 0 && <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: C.gray500 }}>No items.</td></tr>}
          </tbody>
        </table>
        {myOverride && (removeIds.length > 0 || Object.keys(partials).length > 0) && (
          <div style={{ marginTop: 12 }}><Btn variant="purple" onClick={applyEdits}>Apply Changes</Btn></div>
        )}
      </Card>

      <ApprovalTrail approvals={b.approvals} docType="PAYMENT_BATCH" style={{ marginBottom: 0 }} />
    </div>
  );
}
